import React, { useState, useEffect } from 'react';

const PersonalInfo = ({ formData, updateFormData, onValidationChange }) => {
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateFields = () => {
    const newErrors = {};
    
    // Validate name
    if (!formData?.name || formData.name.trim() === '') {
      newErrors.name = 'Name is required';
    }

    // Validate email
    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    
    // Notify parent component about validation status
    const isValid = Object.keys(newErrors).length === 0;
    onValidationChange?.(isValid);
    
    return isValid;
  };

  // Add useEffect to validate on mount and when formData changes
  useEffect(() => {
    validateFields();
  }, [formData?.name, formData?.email]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // For number inputs, convert string to number
    const numericFields = ['household', 'zipcode'];
    const finalValue = numericFields.includes(name) ? Number(value) : value;
    updateFormData('personal', { [name]: finalValue });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const getInputClassName = (fieldName) => {
    return `w-full px-3 py-2 border rounded-md ${errors[fieldName] ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`;
  };

  return (
    <div className="section-container max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-blue-700">Personal Information</h2>
      <p className="text-gray-600 mb-6">
        Please provide your information to help us calculate your water footprint.
      </p>
      
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name: <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData?.name || ''}
              onChange={handleInputChange}
              className={getInputClassName('name')}
              placeholder="Enter your name"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email: <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData?.email || ''}
              onChange={handleInputChange}
              className={getInputClassName('email')}
              placeholder="name@example.com"
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location/City: <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="location"
              value={formData?.location || ''}
              onChange={handleInputChange}
              className={getInputClassName('location')}
              placeholder="Enter your city"
            />
            {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Zip Code: <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="zipcode"
              value={formData?.zipcode || ''}
              onChange={handleInputChange}
              className={getInputClassName('zipcode')}
              placeholder="Enter your zip code"
            />
            {errors.zipcode && <p className="text-red-500 text-sm mt-1">{errors.zipcode}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Housing Type: <span className="text-red-500">*</span>
            </label>
            <select
              name="housingType"
              value={formData?.housingType || ''}
              onChange={handleInputChange}
              className={getInputClassName('housingType')}
            >
              <option value="">Select housing type</option>
              <option value="apartment">Apartment</option>
              <option value="house">House</option>
              <option value="townhouse">Villa</option>
              <option value="other">Other</option>
            </select>
            {errors.housingType && <p className="text-red-500 text-sm mt-1">{errors.housingType}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of people in household: <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="household"
              min="1"
              max="15"
              value={formData?.household || ''}
              onChange={handleInputChange}
              className={getInputClassName('household')}
              placeholder="Enter number of people"
            />
            {errors.household && <p className="text-red-500 text-sm mt-1">{errors.household}</p>}
            <p className="text-sm text-gray-500 mt-1">
              This helps us calculate per-person water usage more accurately
            </p>
          </div>
        </div>

       </div>
    </div>
  );
};

export default PersonalInfo;